import { Dropdown, DropdownItem, DropdownMenu, DropdownTrigger } from "@heroui/dropdown";
import { Tab, Tabs } from "@heroui/tabs";
import { motion } from "framer-motion";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import { useFontStyle } from "~/hook/useFontStyle";
import { useLanguageParams } from "~/hook/useLanguageParams";
import { cn } from "~/lib/utils";
import { COOKIES_LANGUAGE, ICON_ORANGE_COLOR, LANG } from "~/utils/constant";
import { setCookie } from "~/utils/cookies";

type ChangeLanguageProps = {
  variant: "footer" | "header" | "setting";
  isOptionLang?: boolean;
};

const LanguageIcon: FC<{ color?: string }> = ({ color = ICON_ORANGE_COLOR }) => (
  <img src="/images/lang-icon.webp" alt="language icon" className="w-[45px] h-[45px] object-contain select-none" />
);

const ChangeLanguage: FC<ChangeLanguageProps> = ({ variant = "header", isOptionLang = true }) => {
  const lang = useLanguageParams();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { normalInter, medium } = useFontStyle();
  const [isAnimating, setIsAnimating] = useState(false);

  // Haptic feedback function for mobile
  // const triggerHapticFeedback = () => {
  //   if (typeof navigator !== "undefined" && navigator.vibrate) {
  //     navigator.vibrate([10, 5, 10]); // Pattern: vibrate 10ms, pause 5ms, vibrate 10ms
  //   }
  // };

  const handleChange = (newLang: string) => {
    if (newLang === lang) return;

    // Trigger haptic feedback
    // triggerHapticFeedback();

    // Start animation
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 300);

    setCookie(COOKIES_LANGUAGE, newLang, { expires: 365 });
    i18n.changeLanguage(newLang);
    const pathWithoutLang = location?.pathname?.replace(`/${lang}`, `/${newLang}`);
    const wi_search = typeof window !== "undefined" ? window.location.search : "";
    const loc_search = location.search;
    const query = loc_search || wi_search;
    navigate(pathWithoutLang + query, { replace: true });
  };

  useEffect(() => {
    if (lang && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  return (
    <>
      {isOptionLang && (
        <div className={cn("md:hidden", variant === "header" ? "max-md:hidden" : "")}>
          <Dropdown>
            <DropdownTrigger>
              <button className="flex items-center justify-center gap-2 p-1 size-10 cursor-pointer hover:opacity-80 transition-opacity">
                <LanguageIcon />
              </button>
            </DropdownTrigger>
            <DropdownMenu aria-label="Language Selection" onAction={(key) => handleChange(key as string)} selectedKeys={new Set([lang])}>
              <DropdownItem key="en" className={cn("text-textColor hover:!bg-cardBg", normalInter, lang === "en" && "!bg-buttonColor !text-white")}>
                English
              </DropdownItem>
              <DropdownItem
                key="gu"
                className={cn("text-textColor hover:!bg-cardBg font-baloobhai2Regular", lang === "gu" && "!bg-buttonColor !text-white")}
              >
                ગુજરાતી
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </div>
      )}
      <motion.div
        className={cn(
          "relative text-left cursor-pointer",
          isOptionLang ? "hidden md:inline-block" : "inline-block",
          variant === "header" ? "max-md:hidden" : "",
        )}
        animate={isAnimating ? { scale: 0.95, rotate: [0, -2, 2, 0] } : { scale: 1, rotate: 0 }}
        transition={{
          type: "spring",
          stiffness: 400,
          damping: 10,
          duration: 0.1,
        }}
      >
        <Tabs
          aria-label="Tabs colors"
          radius="full"
          color="success"
          selectedKey={lang}
          onSelectionChange={() => handleChange(lang === LANG.english ? LANG.gujarati : LANG.english)}
          classNames={{
            tabList: "gap-[1px] w-full relative rounded-full p-0.5 bg-secondaryBackground border border-borderPrimary transition-all duration-300",
            cursor: "w-full bg-buttonColor transition-all duration-300",
            tabContent: "group-data-[selected=true]:text-[#ffffff] text-textColor transition-colors duration-300",
          }}
        >
          <Tab key="en" title="A" className={cn("text-14 font-semibold", lang === LANG.english ? "text-white" : "text-textColor")} />
          <Tab key="gu" title="અ" className={cn("text-14 font-semibold", lang === LANG.gujarati ? "text-white" : "text-textColor")} />
        </Tabs>
      </motion.div>
    </>
  );
};

export default ChangeLanguage;
